import { Box, Dropdown, DropdownItem } from '@base-app';
import { useAuthStore } from '@modules/auth/store';
import { isUserAdministrator } from '@modules/auth/utils/userRole';
import { canDeactivate, canDelete, canReactivate, isUserDeprovisioned } from '@modules/teams/utils';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionsIconStyled } from './TeamsListItemDropdown.styles';
import { TeamsListItemDropdownProps } from './TeamsListItemDropdown.types';

const ITEM_DROPDOWN_OPTIONS = {
  DEACTIVATE: 'deactivate',
  UPDATE: 'update',
  RESET: 'reset',
  REACTIVATE: 'reactivate',
  REMOVE: 'remove',
  RESET_MFA: 'reset mfa',
};

const TeamsListItemDropdown: FC<TeamsListItemDropdownProps> = ({
  onDeactivate,
  onDelete,
  onMFAReset,
  onReactivate,
  onPasswordReset,
  onUpdate,
  user: rowUser,
}) => {
  const { t } = useTranslation('teams');
  const { user: currentUser } = useAuthStore();

  const items: DropdownItem[] = [];

  if (
    currentUser !== null &&
    isUserAdministrator(currentUser) &&
    !isUserDeprovisioned(rowUser) &&
    !isUserAdministrator(rowUser)
  ) {
    const UpdateUserOption = {
      id: ITEM_DROPDOWN_OPTIONS.UPDATE,
      label: t('update_team_member'),
    };
    items.push(UpdateUserOption);
  }

  if (currentUser !== null && isUserAdministrator(currentUser) && !isUserDeprovisioned(rowUser)) {
    // TODO: We should be able to configure this option for non admin users. Isn't currently abailable by the Okta API.
    const resetPasswordOption = {
      id: ITEM_DROPDOWN_OPTIONS.RESET,
      label: t('reset_password'),
    };
    items.push(resetPasswordOption);
  }

  if (currentUser !== null && isUserAdministrator(currentUser) && canReactivate(rowUser)) {
    const reactivateUserOption = {
      id: ITEM_DROPDOWN_OPTIONS.REACTIVATE,
      label: t('reactivate_team_member'),
    };
    items.push(reactivateUserOption);
  }

  if (currentUser !== null && isUserAdministrator(currentUser) && canDelete(rowUser)) {
    const deleteUserOption = {
      id: ITEM_DROPDOWN_OPTIONS.REMOVE,
      label: t('remove_team_member'),
    };
    items.push(deleteUserOption);
  }

  if (currentUser !== null && isUserAdministrator(currentUser) && !isUserDeprovisioned(rowUser)) {
    const resetPhoneNumber = {
      id: ITEM_DROPDOWN_OPTIONS.RESET_MFA,
      label: t('reset_mfa'),
    };
    items.push(resetPhoneNumber);
  }

  if (currentUser !== null && isUserAdministrator(currentUser) && canDeactivate(rowUser)) {
    const deactivateUserOption = {
      id: ITEM_DROPDOWN_OPTIONS.DEACTIVATE,
      label: t('deactivate_team_member'),
    };
    items.push(deactivateUserOption);
  }

  const handleSelect = (item: DropdownItem): void => {
    if (item.id === ITEM_DROPDOWN_OPTIONS.REMOVE) {
      onDelete();
    }

    if (item.id === ITEM_DROPDOWN_OPTIONS.DEACTIVATE) {
      onDeactivate();
    }

    if (item.id === ITEM_DROPDOWN_OPTIONS.REACTIVATE) {
      onReactivate();
    }

    if (item.id === ITEM_DROPDOWN_OPTIONS.RESET) {
      onPasswordReset();
    }

    if (item.id === ITEM_DROPDOWN_OPTIONS.RESET_MFA) {
      onMFAReset();
    }

    if (item.id === ITEM_DROPDOWN_OPTIONS.UPDATE) {
      onUpdate();
    }
  };

  if (items.length < 1) {
    return null;
  }

  return (
    <Box>
      <Dropdown
        id={`team-list-item-${rowUser.id}`}
        icon={<OptionsIconStyled />}
        items={items}
        onSelect={handleSelect}
      ></Dropdown>
    </Box>
  );
};

export default TeamsListItemDropdown;
